/**
 * Phone number utilities for standardization and formatting
 */

/**
 * Extract normalized phone digits (last 10 digits for US numbers)
 * Used for reliable SMS matching regardless of formatting
 */
export function normalizePhoneDigits(phone: string | null | undefined): string | null {
  if (!phone) return null;
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Extract last 10 digits (US phone number)
  if (digits.length >= 10) {
    return digits.slice(-10);
  }
  
  // Invalid phone number
  return null;
}

/**
 * Standardizes a phone number to the Twilio E.164 format (+1XXXXXXXXXX for US numbers)
 * 
 * This is the canonical function to use when storing phone numbers in the database
 * to ensure consistency across the application.
 * 
 * @param phone The phone number to standardize
 * @returns Standardized phone number in +1XXXXXXXXXX format, or null if invalid
 */
export function standardizePhoneNumber(phone: string | null | undefined): string | null {
  if (!phone) return null;
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');
  
  // Handle different cases
  if (digits.length === 10) {
    // Standard US 10-digit number, add +1 prefix
    return `+1${digits}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    // US number with country code, add + prefix
    return `+${digits}`;
  } else if (digits.length > 11 || digits.length < 10) {
    // Log unusual format but still return a value
    console.warn(`Unusual phone number format: ${phone} (${digits.length} digits)`);
    
    // If we have digits, try to format as best we can
    if (digits.length > 0) {
      return `+${digits}`;
    }
    
    return null;
  } else {
    // Other cases, add + prefix
    return `+${digits}`;
  }
}

/**
 * Format a phone number for display to users
 * This doesn't change the stored format, just presents it in a human-readable way
 * 
 * @param phone The phone number to format
 * @returns Formatted phone number for display
 */
export function formatPhoneForDisplay(phone: string | null | undefined): string {
  if (!phone) return 'Unknown';
  
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Handle different formats based on length
  if (digitsOnly.length === 10) {
    // Format as (XXX) XXX-XXXX
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    // Format as +1 (XXX) XXX-XXXX
    return `+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  } else if (digitsOnly.length > 0) {
    // Return the original if we can't format it nicely
    return phone;
  }
  
  return 'Unknown';
}

/**
 * Validates if a phone number is in a valid format
 * 
 * @param phone The phone number to validate
 * @returns true if the phone number is valid, false otherwise
 */
export function isValidPhoneNumber(phone: string | null | undefined): boolean {
  if (!phone) return false;
  
  const standardized = standardizePhoneNumber(phone);
  if (!standardized) return false;
  
  // A valid standardized US phone number should be in format +1XXXXXXXXXX (12 characters)
  return /^\+1\d{10}$/.test(standardized);
} 